import { useCallback } from "react";

import { createContext, useContext } from "react";
import { SSR_LOOPBACK_BASE_URL } from "lib/const";

export type DictContent = Record<string, unknown>;

type DictContextType = {
  dict: DictContent;
  debug: boolean;
  dictByFormatArgs: DictByFormatArg[];
  setDictByFormatArgs: (args: DictByFormatArg[]) => void;
};

export const DictContext = createContext<DictContextType>({
  dict: {},
  debug: false,
  dictByFormatArgs: [],

  setDictByFormatArgs: () => {},
});

export type DFunction = (key: string, customReplacements?: ReplacementsType) => string;

export const useDict = () => {
  const { dict, debug } = useContext(DictContext);
  // 再レンダリングのたびにdFuncが再生成されるのを防ぐためにuseCallbackを使用
  const dFunc = useCallback(
    (key: string, customReplacements?: ReplacementsType) => d(key, dict, customReplacements || {}, debug),
    [dict, debug],
  );

  return {
    // NOTE: customReplacements は辞書の中で `{{arg1}}` と `{{arg2}}` などの置換対象を指定するためのもの
    // 例: d("key", { arg1: "xxx", arg2: "yyy" }) で辞書のkeyに対して`{{arg1}}`と`{{arg2}}`がある場合、それぞれ`xxx`と`yyy`に置換される
    d: dFunc,
  };
};

export type DictByFormatArg = {
  ns: string; // namespace
  kind: string; // format_kind
  format_id: string | null; // format_id
};

function genD(dict: DictContent, replacements: ReplacementsType, debug: boolean) {
  return (key: string) => d(key, dict, replacements, debug);
}

function d(key: string, dict: DictContent, replacements: ReplacementsType, debug: boolean): string {
  const master = findValue(key, dict["master"] as DictContent);
  const custom = findValue(key, dict["custom"] as DictContent);

  if (debug) {
    return `${custom}[${master}]`;
  }

  const value = custom || master || key;

  return applyReplacements(value, replacements);
}

function findValue(key: string, dict: DictContent) {
  let result: any = dict;

  if (key.split(":").length > 2) {
    return "multiple ns key is not supported" + key;
  }

  const [namespace, nestedKey] = key.split(":");

  // nestedKeyがundefinedの場合（":"が含まれていない場合）はnamespaceのみを使用
  const keys = nestedKey ? [namespace, ...nestedKey.split(".")] : [namespace];

  // 各キーを辞書内で再帰的に検索
  for (const k of keys) {
    // 現在のキーが辞書内に存在するかどうかを確認
    if (result && typeof result === "object" && k in result) {
      result = result[k];
    } else {
      // キーが見つからない場合はnullを返す
      return null;
    }
  }
  return result;
}

export function fetchDict(context: any, namespaces: string[]) {
  return fetch(`${SSR_LOOPBACK_BASE_URL}/api/dict?namespaces=${namespaces}`, {
    headers: { cookie: context.req.headers.cookie },
  }).then((res) => res.json());
}

export function fetchDictWithFormat(namespaces: string[], byFormatArgs: DictByFormatArg[]) {
  //byFormatArgsはrailsがそのままparseできる形にする
  const params = {
    namespaces: namespaces.join(","),
  };
  const dictByFormats: DictByFormatArg[] = byFormatArgs;
  const queryParams = new URLSearchParams(params);
  dictByFormats.forEach((format, index) => {
    Object.entries(format).forEach(([key, value]) => {
      if (value != null) {
        queryParams.append(`dict_by_formats[${index}][${key}]`, value);
      }
    });
  });
  return fetch(`${process.env.NEXT_PUBLIC_FRONTEND_API_BASE_URL}/api/dict?${queryParams.toString()}`, {
    method: "GET",
  }).then((res) => res.json());
}

export type ReplacementsType = { [key: string]: string };

function applyReplacements(str: string, replacements: ReplacementsType): string {
  // 型ガード: strが文字列でない場合のエラーハンドリング
  if (typeof str !== "string") {
    console.error("[dict.ts] applyReplacements: 文字列以外の値が渡されました", {
      type: typeof str,
      value: str,
      stack: new Error().stack,
    });

    // オブジェクトの場合はJSON文字列化してエラーメッセージを返す
    if (typeof str === "object" && str !== null) {
      return `[dict error: received ${JSON.stringify(str).substring(0, 100)}]`;
    }

    return String(str);
  }

  // 空文字も値として置き換える（`||` だと空文字がプレースホルダのまま残る）。値が無い（undefined）ときだけ残す
  return str.replace(/{{\s*(.*?)\s*}}/g, (_, p1) => replacements[p1] ?? `{{${p1}}}`);
}

// setDictByFormatArgsをcomponentsから呼び出すためのhook
export const useSetDictByFormatArgs = () => {
  const { setDictByFormatArgs } = useContext(DictContext);
  return setDictByFormatArgs;
};
